import { LeftMenuService } from 'app/store/services/left-menu.service';
import { Component, HostListener, Input, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';

// Components
import { InfoTipsComponent } from 'app/dialogs/info-tips/info-tips.component';
import { RContentComponent } from 'app/dialogs/quick-response/r-content.component';

// Actions
import { viewMessage } from 'app/store/actions/message.action';
import { openDialog, openSnackbar } from 'app/store/actions/page.action';

// Reducers
import { EReducers, IReducers } from 'app/store/reducers';

// Models
import {
  EMessageStateStatus,
  IMessage,
  IMessageItem,
  IMessageLockDataResponse,
  IMessageState,
  IMessageStateExtraData,
  IMessageWSTopicData,
  MESSAGE_WS_ACTION_LOCK,
  MESSAGE_WS_ACTION_UNLOCK,
  MESSAGES_WS_TOPIC,
} from 'app/store/models/message.model';
import { IUserSessionData, UserType } from 'app/store/models/user.model';
import { Button, ButtonColor, ButtonSize } from 'app/store/models/button.model';
import { EInfoSelectItemType } from 'app/store/models/select.model';
import { DialogData } from 'app/store/models/dialog.model';

// Services
import { SessionService } from 'app/store/services/session.service';
import { ContactsService } from 'app/store/services/contacts.service';
import { updateLeftMenuCount } from 'app/store/actions/meeting.action';
import { MessageService } from 'app/store/services/message.service';
import { RedNoMatchDialogComponent } from '../../dialogs/red-no-match-dialog/red-no-match-dialog.component';
import { ReleaseReplayActionComponent } from './release-replay-action/release-replay-action.component';
import { DialogComponent } from '../../shared/ui/dialog/dialog.component';
import { WebSocketService } from '../../store/services/web-socket.service';
import { vmsMessageLock } from '../../store/actions/user.action';

import * as moment from 'moment';
import { EForwardType, ForwardDialogComponent } from '../../dialogs/forward-dialog/forward-dialog.component';
import { SentToCampignDialogComponent } from '../../dialogs/sent-to-campign-dialog/sent-to-campign-dialog.component';

enum ELockedBannerState {
  SELF,
  OTHER,
}

@Component({
  selector: 'app-view-message',
  templateUrl: './view-message.component.html',
  styleUrls: ['./view-message.component.scss'],
})
export class ViewMessageComponent implements OnInit, OnDestroy {
  @Input() messageId: number;
  messages: IMessage[] = [];
  preMessageId: number;
  nextMessageId: number;
  viewAsPlain = false;
  sessionData: IUserSessionData;
  responseButtonSelect: any;
  contactId: number;
  contactEmail: string;
  contactGroup: string;
  messageStatus: string;
  dataReady = false;
  extraData: IMessageStateExtraData;
  emptyState: boolean;
  vsmResponseButtonProperties: any;
  responseButtonProperties: Button = {
    color: ButtonColor.BLUE,
    size: ButtonSize.NORMAL,
    text: 'Reply',
    onClick: () => this.openNoMatchDialog(),
  };
  managerId?: number;
  vsmRemainingTime: number = 3 * 1000 * 60; // 3 minutes
  showLockedBanner: boolean;
  lockedBannerState: ELockedBannerState;
  locketBannerStates = ELockedBannerState;
  lockedByUser: string;
  currenMessagesPage: number;
  hasMoreMessages: boolean;
  isMessageSpam: boolean;

  private swithingMessage: any;
  private rewrited$: Subject<boolean> = new Subject();
  /**
   * TODO: set TO link, implement CC field, implement attachments
   */

  private destroy$: Subject<boolean> = new Subject<boolean>();
  private vsmIntervalMessageId: undefined | ReturnType<typeof setTimeout>;
  private vsmCurrentMessage: number;
  private comingFrom: string;
  private messageAccountId: number;
  private useLock: boolean;

  constructor(
    private store: Store<IReducers>,
    private router: Router,
    private route: ActivatedRoute,
    private sessionService: SessionService,
    private contactsService: ContactsService,
    private leftMenuService: LeftMenuService,
    private messageService: MessageService,
    private readonly wsService: WebSocketService,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {
    this.leftMenuService.expand('inbox');
  }

  get vsmRemainingMinutes(): string {
    return '0' + Math.floor(this.vsmRemainingTime / 1000 / 60);
  }

  get vsmRemainingSeconds(): string {
    const result = Math.floor(this.vsmRemainingTime / 1000) % 60;

    if (result === 0) {
      return '00';
    } else if (result < 10) {
      return `0${result}`;
    }

    return result.toString();
  }

  @HostListener('window:beforeunload')
  onWindowClose(): void {
    if (
      this.sessionData &&
      this.sessionData.user_type === UserType.VSM &&
      this.vsmCurrentMessage
    ) {
      this.unlockMessage(this.vsmCurrentMessage);
    }
  }

  openNoMatchDialog() {
    this.store.dispatch(
      openDialog({
        dialogData: {
          content: RedNoMatchDialogComponent,
          title: 'No Match Action',
          canClose: true,
          dialogWidth: '900px',
          dialogMaxHeight: '800px',
          componentData: {
            messages: this.messages,
            contactId: this.messages[0].contact_id,
            contactEmail: this.messages[0].contact_email,
            contactFirstName: this.extraData.contact_firstname,
            contactLastName: this.extraData.contact_lastname,
            accountId: this.extraData.message_account,
            userSession: this.sessionData,
            matchedContact: Object.keys(this.extraData.matched_contact).length > 0 ? this.extraData.matched_contact : null,
          },
          onClose: () => {
            window.location.reload();
          },
        },
      }),
    );
  }

  openEscaladeDialog() {
    this.store.dispatch(
      openDialog({
        dialogData: {
          title: 'Escalate action',
          content: 'Are you sure you want to escalate the message?',
          canClose: true,
          dialogWidth: '500px',
          buttons: [
            {
              text: 'Yes',
              actAsClose: true,
              color: ButtonColor.GREEN,
              size: ButtonSize.NORMAL,
              onClick: (dialog: DialogComponent) =>
                this.messageService
                  .sendEscalateReplay(this.messageId)
                  .subscribe(
                    () => {
                      if (this.sessionData.user_type !== UserType.VSM) {
                        this.store.dispatch(
                          openSnackbar({
                            snackbarData: {
                              data: {
                                message: 'Message escalated successfully',
                                type: 'success',
                              },
                            },
                          }),
                        );
                      }
                      dialog.closeDialog();
                      this.router.navigate(['/']);
                    },
                    error =>
                      this.store.dispatch(
                        openSnackbar({
                          snackbarData: {
                            data: {
                              message: error.error,
                              type: 'error',
                            },
                          },
                        }),
                      ),
                  ),
            },
          ],
        },
      }),
    );
  }

  openReleaseDialog() {
    this.store.dispatch(
      openDialog({
        dialogData: {
          title: 'Release action',
          content: ReleaseReplayActionComponent,
          canClose: true,
          dialogWidth: '900px',
          componentData: {
            managerId: this.managerId || null,
          },
          buttons: [
            {
              text: 'Submit',
              color: ButtonColor.GREEN,
              size: ButtonSize.NORMAL,
              onClick: (dialog: DialogComponent) => {
                if (!dialog.dialogForm.valid) {
                  dialog.dialogForm.markAllAsTouched();
                  return;
                }

                this.messageService
                  .sendReleaseReplay(
                    this.messageId,
                    dialog.dialogForm.get('note').value,
                    dialog.dialogForm.get('ccManager').value,
                  )
                  .subscribe(
                    () => {
                      if (this.sessionData.user_type !== UserType.VSM) {
                        this.store.dispatch(
                          openSnackbar({
                            snackbarData: {
                              data: {
                                message: 'Message released successfully',
                                type: 'success',
                              },
                            },
                          }),
                        );
                      }
                      dialog.closeDialog();
                      this.router.navigate(['/']);
                    },
                    error =>
                      this.store.dispatch(
                        openSnackbar({
                          snackbarData: {
                            data: {
                              message: error.error,
                              type: 'error',
                            },
                          },
                        }),
                      ),
                  );
              },
            },
          ],
        },
      }),
    );
  }

  onNavigationButtonClick = (state: 'prev' | 'next') => {
    if (this.vsmCurrentMessage) {
      this.swithingMessage = true;
      this.unlockMessage(this.vsmCurrentMessage);
    }
    if (state === 'prev' && this.preMessageId) {
      this.router.navigate([
        '/messages/view',
        this.preMessageId,
        {
          from: this.comingFrom,
        },
      ]);
    } else if (state === 'next' && this.nextMessageId) {
      this.router.navigate([
        '/messages',
        'view',
        this.nextMessageId,
        {
          from: this.comingFrom,
        },
      ]);
    }
  };


  markAsNotSpam() {
    this.messageService.markAsNotSpam(this.messageId).subscribe(() => {
      this.store.dispatch(
        openSnackbar({
          snackbarData: {
            data: {
              message: 'Message marked as not spam',
              type: 'success',
            },
          },
        }),
      );
      this.isMessageSpam = false;
      this.buildResponseButton();
    });
  }


  /**
   * response is not null and next_email is not null
   */

  buildVSMResponseButtons() {
    let items = [
      {
        label: 'No Match',
        tag: 'no-match',
        type: EInfoSelectItemType.TYPE_LINK,
        onClick: () => this.openNoMatchDialog(),
      },
      {
        label: 'Escalate',
        tag: 'escalade',
        type: EInfoSelectItemType.TYPE_LINK,
        onClick: () => this.openEscaladeDialog(),
      },
      {
          label: 'Release',
          tag: 'release',
          type: EInfoSelectItemType.TYPE_LINK,
          onClick: () => this.openReleaseDialog(),
        }
    ];
    this.vsmResponseButtonProperties = {
      buttonProperties: {
        text: 'Reply',
        size: ButtonSize.SMALL,
        color: ButtonColor.BLUE,
      },
      items: items
    };
    this.changeDetectorRef.detectChanges();
  }

  ngOnInit() {
    this.sessionService
      .onReady()
      .pipe(takeUntil(this.destroy$))
      .subscribe(sessionData => {
        this.route.params.subscribe(({ id, from, manager_id }) => {
          this.rewrited$.next(true);
          this.comingFrom = from;
          this.managerId = manager_id;
          this.sessionData = sessionData;

          if (+id !== this.messageId) {
            this.messageId = +id;
            const data: {
              id_message: number;
              lock?: boolean;
            } = {
              id_message: this.messageId,
            };

            if (
              this.sessionData.user_type === UserType.VSM &&
              !this.wsService.connected
            ) {
              data.lock = true;
              this.useLock = true;
            }

            this.store.dispatch(viewMessage(data));
          }

          this.store
            .select(EReducers.MESSAGE)
            .pipe(
              takeUntil(this.destroy$),
              // take to - loading and ready
              // take(2),
            )
            .subscribe((state: IMessageState) => {
              if (state.status === EMessageStateStatus.READY) {
                if (state.extraData) {
                  if (state.extraData.is_spam !== undefined) {
                    this.isMessageSpam = state.extraData.is_spam;
                  }
                  if (state.extraData.message_account) {
                    this.messageAccountId = state.extraData.message_account;
                  }
                  this.showLockedBanner = true;
                  if (
                    !state.extraData.locked_by ||
                    (state.extraData.locked_by === this.sessionData.id &&
                      this.lockedBannerState !== ELockedBannerState.SELF)
                  ) {
                    this.lockedBannerState = ELockedBannerState.SELF;
                    this.vsmCurrentMessage = state.current_message_id;
                    if (!state.extraData.locked_by) {
                      this.lockMessageIfNeeded(state.current_message_id);
                    } else {
                      this.startLockMessageTimer();
                    }
                  } else if (
                    state.extraData.locked_by !== this.sessionData.id
                  ) {
                    this.lockedBannerState = ELockedBannerState.OTHER;
                    this.lockedByUser = state.extraData.locked_by_name;
                  } else if (!state.extraData.locked_by) {
                    this.lockMessage(state.current_message_id);
                  }
                  this.waitForUnlockMessage(state.current_message_id);
                }
                if (state.current_messages) {
                  this.messages = state.current_messages;
                  this.preMessageId = state.prev_message_id;
                  this.nextMessageId = state.next_message_id;
                  this.extraData = state.extraData;
                  this.currenMessagesPage = state.currentPage;
                  this.hasMoreMessages = state.hasMoreData;
                  if (state.list !== null && state.list.length > 0) {
                    if (!state || !state.list || state.list.length === 0) {
                      this.emptyState = true;
                    } else {
                      this.emptyState = false;
                      let targetMessage: IMessageItem = state.list.find(
                        message =>
                          message.message_id === state.current_message_id,
                      );

                      if (!targetMessage) {
                        targetMessage = state.list[0];
                      }
                      if (this.sessionData.user_type === UserType.SINGLE_USER &&
                        targetMessage.read_status !== true) {
                        this.messageService
                          .readStatus(state.current_message_id)
                          .subscribe();
                      }
                      this.contactId = targetMessage.contact_id;
                      this.contactEmail = targetMessage.contact_email;
                      this.contactGroup = targetMessage.group_name;
                      this.messageStatus = targetMessage.message_status;
                    }
                  } else {
                    if (!this.messages || this.messages.length === 0) {
                      this.emptyState = true;
                    } else {
                      this.emptyState = false;
                      if (this.sessionData.user_type === UserType.SINGLE_USER &&
                        this.messages['0'].read_status !== true) {
                        this.messageService
                          .readStatus(this.messages['0'].message_id)
                          .subscribe();
                      }
                      this.contactId = this.messages['0'].contact_id;
                      this.contactEmail = this.messages['0'].contact_email;
                      this.contactGroup = this.messages['0'].contact_group;
                    }
                  }
                  this.buildVSMResponseButtons();
                  this.dataReady = true;
                }

                this.buildResponseButton();
              }
            });
        });
      });
  }

  buildResponseButton() {
    let opportunities: any = {
      label: 'Opportunities',
      type: EInfoSelectItemType.TYPE_LINK,
      tag: 'main',
      dialogContent: this.getDialogData('opportunities'),
      onClick: () =>
        this.openItemDialog(this.contactId, {
          tag: 'main',
          label: 'Opportunities',
        }),
      children: [
        {
          label: 'Meetings',
          tag: 'meeting',
          type: EInfoSelectItemType.TYPE_LINK,
          dialogContent: this.getDialogData('meeting'),
          onClick: () =>
            this.openItemDialog(this.contactId, {
              tag: 'meeting',
              label: 'Meetings',
            }),
        },
        {
          label: 'Order/Quote',
          tag: 'order',
          type: EInfoSelectItemType.TYPE_LINK,
          dialogContent: this.getDialogData('order'),
          onClick: () =>
            this.openItemDialog(this.contactId, {
              tag: 'order',
              label: 'Order/Quote',
            }),
        },
      ],
    };

    if (this.sessionData.user_type === UserType.VSM) {
      opportunities = {
        label: 'Meetings',
        tag: 'meeting',
        type: EInfoSelectItemType.TYPE_LINK,
        dialogContent: this.getDialogData('meeting'),
        onClick: () =>
          this.openItemDialog(this.contactId, {
            tag: 'meeting',
            label: 'Meetings',
          }),
      };
    }

    let actionItems = [];

    if (!this.isMessageSpam || this.sessionData.user_type !== UserType.VSM) {

      actionItems = [opportunities];

      actionItems = [
        ...actionItems,
        {
          label: 'Cycle',
          tag: 'cycle',
          type: EInfoSelectItemType.TYPE_LINK,
          dialogContent: this.getDialogData('cycle'),
          onClick: () =>
            this.openItemDialog(this.contactId, {
              tag: 'cycle',
              label: 'Cycle',
              cc: this.extraData.all_recipients,
            }),
        },
        {
          label: 'Follow up',
          tag: 'followup',
          type: EInfoSelectItemType.TYPE_LINK,
          dialogContent: this.getDialogData('follow_up'),
          onClick: () =>
            this.openItemDialog(this.contactId, {
              tag: 'followup',
              label: 'Follow up',
              cc: this.extraData.all_recipients,
            }),
        },
        {
          label: 'Inactive',
          tag: 'inactive',
          type: EInfoSelectItemType.TYPE_LINK,
          dialogContent: this.getDialogData('inactive'),
          onClick: () =>
            this.openItemDialog(this.contactId, {
              tag: 'inactive',
              label: 'Inactive',
              cc: this.extraData.all_recipients,
            }),
        },
        {
          label: 'Remove',
          tag: 'remove',
          type: EInfoSelectItemType.TYPE_LINK,
          dialogContent: this.getDialogData('remove'),
          onClick: () =>
            this.openItemDialog(this.contactId, {
              tag: 'remove',
              label: 'Remove',
              cc: this.extraData.all_recipients,
            }),
        },
        {
          label: 'Conversation',
          tag: 'conversation',
          type: EInfoSelectItemType.TYPE_LINK,
          dialogContent: this.getDialogData('conversation'),
          onClick: () =>
            this.openItemDialog(this.contactId, {
              tag: 'conversation',
              label: 'Conversation',
              cc: this.extraData.all_recipients,
            }),
        },
        {
          label: 'Forward',
          tag: 'forward',
          type: EInfoSelectItemType.TYPE_LINK,
          dialogContent: this.getDialogData('forward'),
          onClick: () =>
            this.openItemDialog(this.contactId, {
              tag: 'forward',
              label: 'Forward',
            }),
        },

        // {
        //   label: 'Available',
        //   tag:'available',
        //   type: EInfoSelectItemType.TYPE_LINK,
        //   onClick: () => this.openItemDialog(this.contact_id,{tag:'available',label: 'Available',}),
        // },
        // {
        //   label: 'Future',
        //   tag:'future',
        //   type: EInfoSelectItemType.TYPE_LINK,
        //   onClick: () => this.openItemDialog(this.contact_id,{tag:'future',label: 'Future',}),
        // },
        // {
        //   label: 'Not Available',
        //   tag:'not-available',
        //   type: EInfoSelectItemType.TYPE_LINK,
        //   onClick: () => this.openItemDialog(this.contact_id,{tag:'not-available'}),
        // },
        {
          label: 'Send To Campaign',
          tag: 'send-to-campaign',
          type: EInfoSelectItemType.TYPE_NORMAL,
          onClick: () =>
            this.openItemDialog(this.contactId, {
              tag: 'send-to-campaign',
              label: 'Send to campaign',
            }),
          value: 'sent_to_campaign'
        },
      ];
      if (this.extraData && this.extraData.message_status && this.extraData.reply_action !== 6) {
        actionItems.push({
          label: this.extraData.contact_state,
          tag: 'got-it',
          type: EInfoSelectItemType.TYPE_LINK,
          dialogContent: this.getDialogData('ok_got_it'),
          onClick: () =>
            this.openItemDialog(this.contactId, {
              tag: 'got-it',
              label: 'Keep Current Status',
            }),
        });
      }

      if (this.sessionData && this.sessionData.user_type === 'vsm') {
        actionItems.push({
          label: 'Escalate',
          tag: 'escalate',
          type: EInfoSelectItemType.TYPE_LINK,
          dialogContent: null,
          onClick: () => this.openEscaladeDialog(),
        });

        actionItems.push({
          label: 'Release',
          tag: 'release',
          type: EInfoSelectItemType.TYPE_LINK,
          dialogContent: null,
          onClick: () => this.openReleaseDialog(),
        });
      }
    } else {
      actionItems = [{
          label: 'Release',
          tag: 'release',
          type: EInfoSelectItemType.TYPE_LINK,
          dialogContent: null,
          onClick: () => this.openReleaseDialog(),
        }];
    }
    this.responseButtonSelect = {
      buttonProperties: {
        text: 'Reply',
        size: ButtonSize.SMALL,
        color: ButtonColor.ORANGE,
      },
      items: actionItems,
    };
  }

  ngOnDestroy() {
    if (this.destroy$) {
      this.destroy$.next(false);
    }

    if (this.vsmIntervalMessageId) {
      clearInterval(this.vsmIntervalMessageId);
    }

    if (this.vsmCurrentMessage) {
      this.unlockMessage(this.vsmCurrentMessage);
    }
  }

  isIterable(obj) {
    // checks for null and undefined
    if (obj == null) {
      return false;
    }
    return typeof obj[Symbol.iterator] === 'function';
  }

  getResponseStatus(forBreadcrumbs?: boolean): string {
    if (this.comingFrom && this.comingFrom === 'inbox' && !forBreadcrumbs) {
      return 'Inbox';
    }

    return this.extraData.contact_state || 'Campaign';
  }

  getNextEmailDate(): string {
    if (this.isIterable(this.messages)) {
      for (const message of this.messages) {
        if (message.next_email) {
          return message.next_email;
        }
      }
    }

    return 'Scheduled';
  }

  shouldShowNextServiceDate(): boolean {
    if (this.comingFrom === 'inbox') {
      return false;
    }

    const nextEmail = this.getNextEmailDate();

    if (nextEmail !== 'Scheduled') {
      const now = moment().utc();
      const nextEmailDate = moment(nextEmail);

      if (nextEmailDate < now) {
        return false;
      }
    }
    return ['Remove', 'Inactive'].indexOf(this.getResponseStatus(true)) === -1;
  }

  isUnmatchedMessage(): boolean {
    return (
      this.extraData &&
      this.extraData.message_status &&
      this.extraData.message_status.search(/unmatched_msg/g) !== -1
    );
  }

  getMessageCampaing(): string {
    if (this.messages) {
      for (const message of this.messages) {
        if (message.campaign_name) {
          return message.campaign_name;
        }
      }
    }

    return null;
  }

  hasResponseStatus() {
    return (
      this.getResponseStatus() !== null || this.getNextEmailDate() !== null
    );
  }

  openItemDialog(cid, selectedItem) {
    switch (selectedItem.tag) {
      case 'main':
        break;
      case 'got-it':
        this.contactsService
          .quickResponse({ contact_id: cid }, 'okgotit')
          .subscribe(response => {
            this.store.dispatch(updateLeftMenuCount({ data: {} }));
            this.router.navigate(['/']);
          });
        // v1/messages/okgotit
        break;
      // case 'send-mail':
      //   this.openSendEmailDialog(selectedItem);
      //   break;
      case 'send-to-campaign':
        this.store.dispatch(openDialog({
          dialogData: {
            content: SentToCampignDialogComponent,
            dialogMinWidth: '600px',
            canClose: true,
            title: `Sent to campaign`,
            buttons: [{
              text: 'Send',
              color: ButtonColor.GREEN,
              size: ButtonSize.NORMAL,
              onClick: (dialog) => {
                if (!dialog.component.componentReady) {
                  return false;
                }
                if (!dialog.dialogForm.valid) {
                  dialog.dialogForm.markAllAsTouched();
                  return;
                }

                const campaignId = dialog.dialogForm.value.campaign_id;
                const groupId = dialog.dialogForm.value.group_id;
                const startDate = moment(dialog.dialogForm.value.start_date).format('YYYY-MM-DD');

                this.contactsService.sendContactToCampaign(
                  cid,
                  campaignId,
                  groupId,
                  startDate,
                ).subscribe(
                  () => {
                    this.store.dispatch(openSnackbar({
                      snackbarData: {
                        data: {
                          message: 'Contact successfully send to campaign!',
                          type: 'success',
                        },
                      },
                    }));
                    dialog.closeDialog();
                  },
                  err => this.store.dispatch(openSnackbar({
                    snackbarData: {
                      data: {
                        message: err.error,
                        type: 'error',
                      },
                    },
                  })),
                );
              },
            }],
          },
        }));
        break;
      case 'forward':
        this.store.dispatch(openDialog({
          dialogData: {
            title: `Forward ${this.messageId}`,
            canClose: true,
            content: ForwardDialogComponent,
            componentData: {
              cid,
              selectedItem,
              messageId: this.messageId,
            },
            buttons: [
              {
                text: 'Forward',
                color: ButtonColor.GREEN,
                size: ButtonSize.NORMAL,
                onClick: (dialog: DialogComponent) => {
                  if (!dialog.dialogForm.valid) {
                    dialog.dialogForm.markAllAsTouched();
                    return;
                  }

                  if (dialog.dialogForm.get('forward_type').value === EForwardType.ACCOUNT) {
                    this.contactsService.forwardResponse(this.messageId, dialog.dialogForm.get('contact_details').value).subscribe(() => {
                        this.store.dispatch(
                          openSnackbar({
                            snackbarData: {
                              data: {
                                message: 'Message forwarded to your email address',
                                type: 'success',
                                button: {
                                  text: 'dismiss',
                                },
                              },
                              verticalPosition: 'bottom',
                            },
                          }),
                        );
                        dialog.closeDialog();
                      },
                      err => {
                        this.store.dispatch(openSnackbar({
                          snackbarData: {
                            data: {
                              message: err.error,
                              type: 'error',
                            },
                          },
                        }));
                        dialog.closeDialog();
                      },
                    );
                    return;
                  }

                  this.contactsService.forwardResponseToRecipients(this.messageId, dialog.dialogForm.get('recipients').value, dialog.dialogForm.get('contact_details').value)
                    .subscribe(() => {
                        this.store.dispatch(
                          openSnackbar({
                            snackbarData: {
                              data: {
                                message: `Message forwarded to the following recipients: ${dialog.dialogForm.get('recipients').value}`,
                                type: 'success',
                              },
                            },
                          }),
                        );
                        dialog.closeDialog();
                      },
                      err => {
                        this.store.dispatch(openSnackbar({
                          snackbarData: {
                            data: {
                              message: err.error,
                              type: 'error',
                            },
                          },
                        }));
                        dialog.closeDialog();
                      });
                },
              },
            ],
          },
        }));

        break;
      default:
        this.openDialog(cid, selectedItem);
        break;
    }
  }

  getDialogData(type): DialogData {
    return {
      content: InfoTipsComponent,
      title: 'Tips',
      canClose: true,
      dialogMinWidth: '700px',
      componentData: {
        type,
      },
    };
  }

  openDialog(cid, data) {
    data.contact_email = this.contactEmail;
    this.store.dispatch(
      openDialog({
        dialogData: {
          title: data.label,
          content: RContentComponent,
          componentData: {
            data,
            messageId: this.messageId,
            contact_id: cid,
            redirectRoute: '/messages',
            accountId:
              this.sessionData && this.sessionData.user_type === UserType.VSM && this.messageAccountId ?
                this.messageAccountId :
                null,
            overrideRecipients: data.tag && data.tag === 'meeting' ? this.extraData.account_email : null,
          },
          dialogMinWidth: '1000px',
          canClose: true,
          tips: ['cycle', 'followup', 'meeting', 'inactive', 'remove'].indexOf(data.tag) !== -1 ? 'send-update' : null,
        },
      }),
    );
  }

  openAddToCampaignDialog(data) {
    this.store.dispatch(
      openDialog({
        dialogData: {
          title: data.label,
          content: RContentComponent,
          componentData: {
            data,
            messageId: this.messageId,
          },
          dialogMinWidth: '1000px',
          canClose: true,
          buttons: [
            {
              text: 'Ok',
              actAsClose: true,
              color: ButtonColor.GREEN,
              size: ButtonSize.NORMAL,
            },
            {
              text: 'Close',
              actAsClose: true,
              color: ButtonColor.RED,
              size: ButtonSize.NORMAL,
              onClick: () => console.log('cska'),
            },
          ],
        },
      }),
    );
  }

  getBackRoute(): any[] {
    if (this.comingFrom) {
      if (this.comingFrom === 'inbox') {
        return ['/messages'];
      }
      return ['/messages', this.comingFrom];
    }

    return null;
  }

  getMessagesPage(toAdd: number) {
    this.store.dispatch(viewMessage({
      page: this.currenMessagesPage + toAdd,
      id_message: this.messageId,
      lock: this.useLock,
    }));
    window.scrollTo({
      top: 0,
    });
  }

  private waitForUnlockMessage(messageId: number) {
    if (!this.wsService.connected) {
      return;
    }
    if (!messageId && this.messageId) {
      messageId = this.messageId;
    }

    this.wsService
      .on<IMessageLockDataResponse>(MESSAGES_WS_TOPIC)
      .pipe(takeUntil(this.destroy$), takeUntil(this.rewrited$))
      .subscribe(({ action, message_id, account_id, account_name, success, error }) => {
        if (message_id !== messageId) {
          return;
        }
        if (action === MESSAGE_WS_ACTION_UNLOCK) {
          this.lockedBannerState = null;
          this.lockedByUser = null;
          this.vsmRemainingTime = 3 * 1000 * 60;
          clearInterval(this.vsmIntervalMessageId);
          this.vsmIntervalMessageId = undefined;
          if (this.swithingMessage) {
            this.swithingMessage = false;
          }
          this.lockMessageIfNeeded(message_id);
        } else if (action === MESSAGE_WS_ACTION_LOCK) {
          if (account_id === this.sessionData.id) {
            if (!success) {
              this.store.dispatch(openDialog({
                dialogData: {
                  content: `<b>${error || ''}</b><br/>Could not process the message. Please, contact the administrator first!`,
                  canClose: true,
                  title: 'Message process error',
                },
              }));
              this.router.navigate(['/messages']);
              return;
            }
            this.lockedBannerState = ELockedBannerState.SELF;
            this.lockedByUser = account_name;
            this.vsmRemainingTime = 3 * 1000 * 60;
            clearInterval(this.vsmIntervalMessageId);
            this.vsmIntervalMessageId = undefined;
            this.startLockMessageTimer();
            return;
          }
        }
      });
  }

  private lockMessage(messageId: number) {
    this.wsService.send<IMessageWSTopicData>({
      topic: MESSAGES_WS_TOPIC,
      data: {
        action: MESSAGE_WS_ACTION_LOCK,
        message_id: messageId,
      },
    });
    this.store.dispatch(vmsMessageLock({
      messageId,
    }));
  }

  private lockMessageIfNeeded(messageId: number) {
    this.vsmCurrentMessage = messageId;
    if (!this.vsmCurrentMessage && this.messageId) {
      this.vsmCurrentMessage = this.messageId;
    } else if (!this.vsmCurrentMessage) {
      return;
    }

    this.lockMessage(this.vsmCurrentMessage);

    this.startLockMessageTimer();
  }

  private startLockMessageTimer() {
    if (this.sessionData.user_type !== UserType.VSM) {
      return;
    }
    if (!this.vsmIntervalMessageId) {
      this.vsmIntervalMessageId = setInterval(() => {
        this.vsmRemainingTime -= 1000;

        if (this.vsmRemainingTime <= 0) {
          this.unlockMessage(this.vsmCurrentMessage);
          this.store.dispatch(
            openSnackbar({
              snackbarData: {
                data: {
                  message: 'Your time is over for this message!',
                  type: 'error',
                },
              },
            }),
          );
          this.router.navigate(['/messages']);
        }
      }, 1000); // 3 minutes
    }
  }

  private unlockMessage(messageId: number): void {
    if (!this.wsService.connected) {
      // nothing to do after this, because the message will be merked as unlocked
      // if the socket server is not running
      this.messageService.viewMessage(messageId, undefined, true).subscribe();
      return;
    }
    this.wsService.send<IMessageWSTopicData>({
      topic: MESSAGES_WS_TOPIC,
      data: {
        action: MESSAGE_WS_ACTION_UNLOCK,
        message_id: messageId,
      },
    });
  }
}
